/**
 * Vault Pro Service
 * API functions for vault transfer operations
 */

import axios from 'axios';
import { API } from '@/utils/helper';
import { getUID } from '@/utils/helper';

/**
 * Get available lock period options with rates
 * @returns {Promise<Array<{value: string, label: string, days: number, rate: number}>>}
 */
export const getLockOptions = async () => {
  const response = await axios.get(`${API}/vault/lock-options`);
  return response.data;
};

/**
 * Transfer funds from wallet to vault with lock period
 * @param {string} coin - Coin symbol (BTC, ETH, etc.)
 * @param {number} amount - Amount to transfer
 * @param {string} lockPeriod - Lock period key (7d, 30d, 6m, 1y)
 * @returns {Promise}
 */
export const transferIn = async (coin, amount, lockPeriod) => {
  const response = await axios.post(`${API}/vault/transfer-in`, {
    uid: getUID(),
    coin,
    amount: Number(amount),
    lock_period: lockPeriod,
  });
  return response.data;
};

/**
 * Transfer funds from vault back to wallet (by deposit ID)
 * @param {string} coin - Coin symbol (BTC, ETH, etc.)
 * @param {number} depositId - Deposit ID to withdraw
 * @returns {Promise}
 */
export const transferOut = async (coin, depositId) => {
  const response = await axios.post(`${API}/vault/transfer-out`, {
    uid: getUID(),
    coin,
    deposit_id: depositId,
  });
  return response.data;
};

/**
 * Get vault data with all deposits for a specific coin
 * @param {string} coin - Coin symbol
 * @returns {Promise<{totalBalance: number, deposits: Array}>}
 */
export const getVaultBalance = async (coin) => {
  const response = await axios.post(`${API}/vault/vault-data`, {
    uid: getUID(),
    coin,
  });
  return response.data;
};

/**
 * Get vault interest earnings history
 * @returns {Promise<Array>}
 */
export const getInterestHistory = async () => {
  const response = await axios.post(`${API}/vault/history`, {
    uid: getUID(),
  });
  return response.data;
};

/**
 * Get vault transfer in/out history
 * @returns {Promise<Array>}
 */
export const getTransactionHistory = async () => {
  const response = await axios.post(`${API}/vault/transactions`, {
    uid: getUID(),
  });
  return response.data;
};
